'use client'

import { useState } from 'react'

import { Button } from '@/core/components/form/button/button'
import { InputText } from '@/core/components/form/input-text'
import { Textarea } from '@/core/components/form/textarea'
import { toast } from '@/core/lib/toast'

import {
  Cond,
  SheetHead,
} from '@/features/veluvit/components/overlay/layer-parts'
import { Chip } from '@/features/veluvit/components/ui/atoms'
import {
  INPUT_ZOOM_SAFE,
  TEXTAREA_ZOOM_SAFE,
} from '@/features/veluvit/components/ui/field-class'
import { api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Bootstrap } from '@/features/veluvit/types/api.types'

/* 네 가지뿐이라 드롭다운을 열게 하지 않고 한눈에 고르게 한다 */
const CATEGORIES = [
  { value: 'question', label: '이용 문의' },
  { value: 'bug', label: '오류 제보' },
  { value: 'report', label: '신고' },
  { value: 'refund', label: '재화 문의' },
]

/** 고객센터 — 문의 유형·제목·내용, 방에서 열면 최근 대화 4개가 함께 전달된다 (v2 ACT.v2Support) */
export default function SupportSheet({ roomId }: { roomId?: string }) {
  const tickets = useSessionStore((s) => s.data?.tickets || [])
  const [category, setCategory] = useState('question')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  return (
    <>
      <SheetHead title="고객센터" />
      <form
        className="mt-4.5 grid gap-3 text-left"
        onSubmit={async (event) => {
          event.preventDefault()
          setPending(true)
          setError('')
          try {
            const data = await api<Bootstrap>('/ticket/', {
              category,
              subject,
              body,
              ...(roomId ? { roomId } : {}),
            })
            useSessionStore.getState().applyBootstrap(data)
            setSubject('')
            setBody('')
            toast.show('문의가 접수됐어요')
          } catch (e) {
            setError(e instanceof Error ? e.message : '다시 시도해 주세요.')
          } finally {
            setPending(false)
          }
        }}
      >
        <div>
          <p
            id="support-category"
            className="mb-2 block text-c14 font-medium text-foreground"
          >
            문의 유형
          </p>
          <div
            role="group"
            aria-labelledby="support-category"
            className="flex flex-wrap gap-2"
          >
            {CATEGORIES.map((c) => (
              <Chip
                key={c.value}
                on={category === c.value}
                aria-pressed={category === c.value}
                onClick={() => setCategory(c.value)}
              >
                {c.label}
              </Chip>
            ))}
          </div>
        </div>
        <InputText
          label="제목"
          classNames={INPUT_ZOOM_SAFE}
          name="subject"
          maxLength={120}
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <Textarea
          label="내용"
          classNames={TEXTAREA_ZOOM_SAFE}
          name="body"
          maxLength={5000}
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="text-c12 text-t3">
          {roomId
            ? '문제 확인을 위해 이 방의 최근 대화 4개가 함께 전달돼요.'
            : '문의와 답변은 계정에 저장됩니다.'}
        </p>
        <Button
          variant="accent"
          size="lg"
          shape="pill"
          bold
          className="w-full"
          type="submit"
          disabled={pending}
        >
          문의 보내기
        </Button>
        <p
          role="alert"
          className="text-c13 leading-[1.6] whitespace-pre-wrap text-error"
        >
          {error}
        </p>
      </form>
      <div>
        {tickets.map((t) => (
          <div
            key={t.id}
            className="py-3.5 shadow-[inset_0_-1px_0_rgb(255_255_255/0.08)]"
          >
            <b>{t.subject}</b>
            <p className="text-c13 leading-[1.7] whitespace-pre-wrap">
              {t.body}
            </p>
            <span className="text-c12 text-t3">{t.status || '접수'}</span>
            {t.replies.map((r) => {
              const text = r.text || r.body || r.reply || ''
              return (
                <Cond key={text} className="mt-2">
                  {text}
                </Cond>
              )
            })}
          </div>
        ))}
      </div>
    </>
  )
}
